import { useEffect, useState } from "react";

const _ = require("lodash");

/**
 * Generic dirty-state tracker for config panels.
 *
 * Reports whether `current` deviates from the baseline captured from `initial`.
 * Each call site decides what counts as a change by shaping `initial`/`current`
 * (e.g. pass already-filtered values so cosmetic empties don't register).
 *
 * - `initial`: the saved/original values. Re-captured whenever it changes by
 *   value, so an async load, backend reload, or post-save reset re-baselines
 *   correctly. The baseline lives in state (not a ref) so that re-baselining
 *   triggers a re-render and `isDirty` recomputes — otherwise a baseline that
 *   populates after the first render would leave the panel stuck "dirty".
 * - `current`: the live, in-progress values.
 * - `onDirtyChange`: called with the boolean dirty state (e.g. setModalSaveStatus).
 *
 * Returns the boolean too, for inline use.
 */
export const useDirtyState = (initial, current, onDirtyChange) => {
  const initialKey = JSON.stringify(initial);
  const [baseline, setBaseline] = useState(initial);
  const baselineKey = JSON.stringify(baseline);

  // Re-capture the baseline whenever `initial` changes by value.
  useEffect(() => {
    if (initialKey !== baselineKey) setBaseline(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  // While the baseline is catching up to a changed `initial`, report clean to
  // avoid a one-render false positive (matters when the baseline loads async).
  const isDirty = initialKey === baselineKey && !_.isEqual(baseline, current);

  useEffect(() => {
    if (typeof onDirtyChange === "function") onDirtyChange(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  return isDirty;
};

export default useDirtyState;
